import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../config/api';
import { User, Settings, LogOut, Building, Check, Plus } from 'lucide-react';

const UserMenu = ({ compact = false }) => {
  const { user, logout, organization, switchOrganization } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  // Calculate position for the dropdown
  const [menuPosition, setMenuPosition] = useState({ left: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        left: rect.right + 8,
      });
    }
  }, []);

  // Fetch organizations on mount (like OrganizationSwitcher does)
  useEffect(() => {
    fetchUserOrganizations();
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      
      window.addEventListener('resize', updatePosition);
      return () => window.removeEventListener('resize', updatePosition);
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showCreateModal) {
          closeCreateModal();
        } else if (isOpen) {
          setIsOpen(false);
        }
      }
    };

    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target) && 
          buttonRef.current && !buttonRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen || showCreateModal) {
      document.addEventListener('keydown', handleEscape);
    }
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showCreateModal]);

  const fetchUserOrganizations = async () => {
    try {
      const response = await apiRequest('/auth/organizations');
      if (response.ok) {
        const orgs = await response.json();
        setOrganizations(orgs);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleSwitchOrganization = async (orgId) => {
    if (orgId === organization?.id) {
      setIsOpen(false);
      return;
    }

    setLoading(true);
    try {
      await switchOrganization(orgId);
      setIsOpen(false);
    } catch (error) {
      console.error('Error switching organization:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const openCreateModal = () => {
    setIsOpen(false);
    setShowCreateModal(true);
    setNewOrgName('');
    setCreateError('');
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewOrgName('');
    setCreateError('');
  };

  const handleCreateOrganization = async (e) => {
    e.preventDefault();
    setCreateError('');
    
    if (!newOrgName.trim()) {
      setCreateError('Organization name is required');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await apiRequest('/auth/organizations', {
        method: 'POST',
        body: JSON.stringify({ name: newOrgName.trim() }),
      });

      if (response.ok) {
        const newOrg = await response.json();
        
        // Refresh organizations list
        await fetchUserOrganizations();
        
        // Switch to the new organization
        await switchOrganization(newOrg.id);
        
        // Close modal
        closeCreateModal();
        
        // Navigate to settings page (like OrganizationSwitcher does)
        navigate('/settings');
      } else {
        const error = await response.json();
        setCreateError(error.detail || 'Failed to create organization');
      }
    } catch (error) {
      console.error('Error creating organization:', error);
      setCreateError('Error creating organization');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      {/* Avatar Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center ${compact ? 'justify-center p-2' : 'space-x-2 px-3 py-2'} text-sm text-gray-600 dark:text-gray-200 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 rounded-md transition-colors ${isOpen ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
      >
        <div className="w-8 h-8 bg-brand-100 dark:bg-brand-700 rounded-full flex items-center justify-center">
          <User className="w-4 h-4 text-brand-600 dark:text-brand-200" />
        </div>
        {!compact && (
          <span className="max-w-32 truncate font-medium">
            {user?.full_name || user?.username}
          </span>
        )}
      </button>

      {/* Floating Dropdown Menu */}
      {isOpen && (
        <div
          ref={menuRef}
          className="fixed z-[100] animate-in fade-in slide-in-from-bottom-2 duration-200"
          style={{
            bottom: '10px',
            left: `${menuPosition.left}px`,
          }}
        >
          <div className="w-80 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* User Info Header */}
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-brand-100 dark:bg-brand-700 rounded-full flex items-center justify-center">
                  <User className="w-5 h-5 text-brand-600 dark:text-brand-200" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {user?.full_name || user?.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Organization Switcher - Like OrganizationSwitcher component */}
            <div className="border-b border-gray-100 dark:border-gray-700">
              <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-700 dark:text-gray-400">
                {organizations.length <= 1 ? 'Current Organization' : 'Switch Organization'}
              </div>
              
              {organizations.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  No organizations found
                </div>
              ) : (
                <div className="max-h-48 overflow-y-auto py-1">
                  {organizations.map((org) => (
                    <button
                      key={org.id}
                      onClick={() => handleSwitchOrganization(org.id)}
                      className="w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-gray-50 transition-colors dark:hover:bg-gray-700"
                      disabled={loading}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center overflow-hidden dark:bg-brand-700">
                          {org.logo_url ? (
                            <img 
                              src={org.logo_url} 
                              alt={`${org.name} logo`}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <Building className="w-4 h-4 text-brand-600 dark:text-brand-200" />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{org.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{org.slug}</div>
                        </div>
                      </div>
                      {organization?.id === org.id && (
                        <Check className="w-4 h-4 text-brand-600 dark:text-brand-400" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              <div className="border-t border-gray-100 dark:border-gray-700">
                <button
                  onClick={openCreateModal}
                  className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-left hover:bg-brand-50 transition-colors text-brand-600 font-medium dark:hover:bg-brand-900 dark:text-brand-400"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create New Organization</span>
                </button>
              </div>
            </div>

            {/* Menu Actions */}
            <div className="p-1">
              <Link
                to="/settings"
                onClick={() => setIsOpen(false)}
                className="flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Settings className="w-4 h-4 mr-3 text-gray-400 dark:text-gray-500" />
                Settings
              </Link>
              
              <button
                onClick={handleLogout}
                className="w-full flex items-center px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4 mr-3 text-gray-400 dark:text-gray-500" />
                Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal - Like OrganizationSwitcher */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-50">
          <div className="absolute inset-0" onClick={closeCreateModal} aria-hidden="true"></div>
          <div 
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 z-[111]"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-org-title"
          >
            <h2 
              id="create-org-title" 
              className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4"
            >
              Create New Organization
            </h2>
            <form onSubmit={handleCreateOrganization} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="Enter organization name"
                  className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  autoFocus
                  disabled={createLoading}
                />
                {createError && (
                  <p className="text-sm text-rose-600 dark:text-rose-400 mt-1">{createError}</p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={createLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading || !newOrgName.trim()}
                  className="px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {createLoading ? 'Creating...' : 'Create Organization'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default UserMenu;
